import React, { SyntheticEvent, useState, useEffect, useRef } from 'react';
import { Grid, SearchBar, SearchContext, SearchContextManager } from '@giphy/react-components';
import type { LexicalEditor } from 'lexical';
import { INSERT_IMAGE_COMMAND } from '../../plugins/MediaPlugin';
import { useMediaQuery } from '@chakra-ui/react';
import ButtonCustom from '../../../../components/Button/index';
import { FORMAT_ELEMENT_COMMAND } from 'lexical';
import giphyAttribution from '../../../../assets/giphy-black.png';  


interface GiphySearchProps {
  activeEditor: LexicalEditor;
  onClose: () => void;
  isCommentEditor?: boolean;
}

const GiphySearchContent = ({ activeEditor, onClose, isCommentEditor }: GiphySearchProps) => {
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const [isDisabled, setIsDisabled] = useState(true);
  const [selectedGIF, setSelectedGIF] = useState<null | { title: any; images: any; id: any }>(null);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(''); 
  const debounceValue = 500;

  const handleError = (error: any) => {
    switch (error.status) {
      case 400:
        setErrorMessage('Bad Request. Check request format');
        setIsError(true);
        break;
      case 401:
        setErrorMessage('Unauthorized. Check API Key');
        setIsError(true);
        break;
      case 403:
        setErrorMessage('Forbidden. Check API Key');
        setIsError(true);
        break;
      case 404:
        setErrorMessage('Not Found. No GIF with this ID');
        setIsError(true);
        break;
      case 414:
        setErrorMessage('URI too long. Search query exceeds 50 characters');
        setIsError(true);
        break;
      case 429:
        setErrorMessage('Too Many Requests. API Key may require upgrading to Production');
        setIsError(true);
        break;
      case 200:
        setErrorMessage('');
        setIsError(false);
        break;
      default:
        setErrorMessage('');
        setIsError(false);
        break;
    }
  };

  const handleGifClick = (gif: any, e: SyntheticEvent) => {
    var element = document.querySelectorAll('[data-giphy-id]');
    for (let i = 0; i < element.length; i++) {
      if (element[i].classList.contains('giphy-gif-selected')) {
        element[i].classList.remove('giphy-gif-selected');
      }
    }
    e.currentTarget.className += ' giphy-gif-selected';

    let gifObject = {};
    gifObject = gif;
    setSelectedGIF((gif) => ({
      ...gif,
      ...gifObject
    }));
    setIsDisabled(false);
  };

  const handleAddClick = () => {
    const payload = {
      altText: selectedGIF!.title,
      src: selectedGIF!.images.original.url,
      imageType: 'gif'
    };

    if (isCommentEditor != true) {
      activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
    }
    activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
    if (isCommentEditor != true) {
      activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
    }

    onClose();

    return payload;
  };

  return (
    <div>
      {isError ? (
        <div className="w-full flex flex-col gap-6">
          <div className="w-full max-w-3xl mx-auto p-4 flex items-center justify-center">
            <p className="mt-0 2xl:mt-5 text-xl font-black">
              Sorry, we've run into an error! Please try again later!
            </p>
          </div>
        </div>
      ) : (
        <div className="w-full flex flex-col gap-4">
          <div className="flex flex-col">
            <SearchBar searchDebounce={debounceValue} />
          </div>
          <SearchContext.Consumer>
            {({ fetchGifs, searchKey }) => (
              <Grid
                key={searchKey}
                columns={isMobile ? 2 : 3}
                width={isMobile ? 300 : 600}
                onGifsFetchError={handleError}
                fetchGifs={fetchGifs}
                noResultsMessage={
                  <div className="flex items-center justify-center">
                    <p className="mt-0 2xl:mt-5 text-xl font-black">
                      No results found! Try another search term!
                    </p>
                  </div>
                }
                onGifClick={handleGifClick}
                noLink={true}
                hideAttribution={false}
                className="giphy-grid"
              />
            )}
          </SearchContext.Consumer>
          <div className="flex items-center justify-between gap-6">
            <ButtonCustom className="w-full" variant="outline" onClick={onClose}>
              Discard
            </ButtonCustom>
            <ButtonCustom disabled={isDisabled} className="w-full" onClick={handleAddClick}>
              Add
            </ButtonCustom>
          </div>
        </div>
      )}
    </div>
  );
};

const GiphySearch = ({ activeEditor, onClose, isCommentEditor }: GiphySearchProps) => {
  // Reference to track if we've added the attribution
  const attributionAddedRef = useRef(false);
  
  useEffect(() => {
    if (attributionAddedRef.current) return;
    
    const modalTitle = document.querySelector('h2.Modal__title');
    if (modalTitle) {
      // Create the attribution image element
      const img = document.createElement('img');
      img.src = giphyAttribution;
      img.alt = 'Powered by GIPHY';
      img.style.height = '20px';
      img.style.marginLeft = '10px';
      
      const wrapper = document.createElement('div');
      wrapper.style.display = 'flex';
      wrapper.style.alignItems = 'center';
      wrapper.style.width = '100%';
      wrapper.style.justifyContent = 'space-between';
      
      const titleContent = modalTitle.cloneNode(true);
      
      while (modalTitle.firstChild) {
        modalTitle.removeChild(modalTitle.firstChild);
      }
      
      // Add the title content and image to the wrapper
      wrapper.appendChild(titleContent);
      wrapper.appendChild(img);
      
      modalTitle.appendChild(wrapper);
      
      attributionAddedRef.current = true;
    }
  }, []);

  return (
    <div className="w-full max-w-3xl mx-auto p-4">
      <SearchContextManager apiKey={`${process.env.REACT_APP_GIPHY_API_KEY}`}>
        <GiphySearchContent
          activeEditor={activeEditor}
          onClose={onClose}
          isCommentEditor={isCommentEditor}
        />
      </SearchContextManager>
    </div>
  );
};

/*
Error handling snippet, revisit for error 414
async (offset: number) => { //fetchGifs: (offset: number) => Promise<GifsResult>;
    try {
        return await fetchGifs(offset);
    } catch (err) {
        console.log(err);
        Error(err as Error); //<- handle possible errors there
        return {data: [],
            meta: null,
            pagination: null};
    }
}
*/

export default GiphySearch;
